import type {Logger as PinoLogger} from 'pino';
import Pino from 'pino';

import {LogLevels, type Logger, type LogLevelsString} from './baseLogger';
import {createRedactor, type Redactor} from './redactor';
export * from './redactor';

const REDACTED_REPLACEMENT = '[REDACTED]';

export type {Logger, LogLevelsString};
export interface ChildLogger extends Logger {
    child(bindings: Record<string, unknown>): Logger;
}

export interface PexLogger extends ChildLogger {
    /**
     * Set this property to the desired logging level. In order of priority, available levels are:
        1. trace
        2. debug
        3. info
        4. warn
        5. error
        6. fatal
        7. silent

        The logging level is a minimum level. For instance if logger.level is 'info' then all 'fatal', 'error', 'warn', and 'info' logs will be enabled.

        You can pass 'silent' to disable logging.
     */
    level: string;
    /**
     * Get the log file as a blob
     */
    getLogs(): Blob;
    /**
     * Get a file name based on (app) name, current date/time, and extension
     */
    getLogFileName(): string;
    /**
     * Get the log file as a string.
     */
    getLogFile(): string;
    /**
     * Get Log Event Object
     **/
    logEvents: string[];
    /**
     * Create a log file based on the log until now, and trigger a download
     */
    downloadLog(): void;
}

export function stringifyReplacer(_key: string, value: unknown) {
    if (value instanceof HTMLElement) {
        const innerHTML = value.innerHTML;
        if (innerHTML !== '') {
            return value.outerHTML.replace(innerHTML, '{...}');
        } else {
            return value.outerHTML;
        }
    } else if (value instanceof Function) {
        return `[Function ${value.name}]`;
    }
    return value;
}

interface Options {
    /**
     * The name of the logger. When set adds a name field to every JSON line logged
     */
    name?: string;
    /**
     * Minimum level to be logged
     */
    minLevel?: LogLevelsString;
    /**
     * Name to prefix the file with for the output log file
     * @defaultValue 'pexip'
     */
    fileName?: string;
    /**
     * Extension (without the dot) to use for the output log file
     * @defaultValue 'log'
     */
    fileExt?: string;

    redactor?: Redactor;
}

const isLogLevelKey = (t: string | undefined): t is LogLevelsString =>
    typeof t === 'string' && t in LogLevels;

// For node `process` is defined. For vite (ie. in the browser) `process`
// is undefined, but `process.env.LOG_LEVEL` etc is defined because it's
// replaced in the build.
//
// For cypress `process.env.NODE_ENV` is defined, but `process` is not, and
// `process.env.LOG_LEVEL` is not.
const isCypress = (window as {Cypress?: unknown}).Cypress;
const LOG_LEVEL = isCypress
    ? 'info'
    : isLogLevelKey(process.env.LOG_LEVEL)
      ? process.env.LOG_LEVEL
      : 'debug';

export const createLogger = ({
    minLevel = LOG_LEVEL,
    fileName = 'pexip',
    fileExt = 'log',
    redactor = createRedactor({
        defaultReplaceTo: REDACTED_REPLACEMENT,
        keysToRedact: [],
    }),
}: Options = {}): PexLogger => {
    const logEvents: string[] = [];

    function getLogs() {
        return new Blob(
            logEvents,
            // Technically application/ld+json
            {type: 'text/plain; charset=utf-8'},
        );
    }

    function getLogFile(): string {
        return logEvents.join('');
    }

    function getLogFileName(name = fileName, ext = fileExt) {
        return `${name}-${new Date().toISOString().replace(/:/g, '-')}.${ext}`;
    }

    function downloadLog(fileName = getLogFileName()) {
        const blobUrl = URL.createObjectURL(getLogs());
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = fileName;
        link.click();
        // Wait half a second, as reportedly some browsers will fail if it's revoked too quickly
        setTimeout(() => URL.revokeObjectURL(blobUrl), 500);
    }

    // HACK: Stupid solution, but seems to be the best way to transform
    //       transmit format to what e.g. pino-pretty expects.
    const pinoStore = Pino({
        browser: {
            write: (evt: unknown) => {
                if (typeof evt !== 'object' || !evt) {
                    return;
                }
                try {
                    const logEvent = JSON.stringify(
                        redactor.redactObj(evt as Record<string, unknown>),
                        stringifyReplacer,
                    );
                    logEvents.push(logEvent);
                    logEvents.push('\n');
                } catch {
                    const logEvent = JSON.stringify(
                        redactor.redactObj(
                            Object.fromEntries(
                                Object.entries(evt).map(([k, v]) => [
                                    k,
                                    typeof v === 'object' ? `${v}` : v,
                                ]),
                            ),
                        ),
                    );
                    logEvents.push(logEvent);
                    logEvents.push('\n');
                }
            },
        },
        level: minLevel,
    });

    const pino = Pino({
        browser: {
            transmit: {
                send: (level, event) => {
                    const [first, ...rest] = event.messages; // work around ts typechecking
                    event.bindings
                        .reduce<Pino.Logger>(
                            (ch, bi) => ch.child(bi),
                            pinoStore,
                        )
                        [level](first, ...rest);
                },
            },
        },
        level: minLevel,
    });

    const addPattern = (value: string, redactTo = REDACTED_REPLACEMENT) =>
        redactor.addPattern(value, redactTo);

    return {
        get logEvents() {
            return logEvents;
        },
        set level(level: typeof pino.level) {
            pinoStore.level = level;
            pino.level = level;
        },
        getLogs,
        getLogFileName,
        getLogFile,
        downloadLog,
        addRedactPattern: addPattern,
        redact: redactor.redactStr,
        fatal: (meta, message) => pino.fatal(meta, message),
        error: (meta, message) => pino.error(meta, message),
        warn: (meta, message) => pino.warn(meta, message),
        info: (meta, message) => pino.info(meta, message),
        debug: (meta, message) => pino.debug(meta, message),
        trace: (meta, message) => pino.trace(meta, message),
        silent: (meta, message) => pino.silent(meta, message),
        child: (...args: Parameters<PinoLogger['child']>) => {
            const child = pino.child(...args);
            return {
                fatal: (meta, message) => child.fatal(meta, message),
                error: (meta, message) => child.error(meta, message),
                warn: (meta, message) => child.warn(meta, message),
                info: (meta, message) => child.info(meta, message),
                debug: (meta, message) => child.debug(meta, message),
                trace: (meta, message) => child.trace(meta, message),
                silent: (meta, message) => child.silent(meta, message),
                redact: redactor.redactStr,
                addRedactPattern: addPattern,
            };
        },
    };
};
